import * as React from 'react';
import Modal from '../../components/common/modal';
import * as MergeMembers from '../../core/gql/mutations/merge-members';

import { useMutation } from '@apollo/client';

import { Panel, PanelHeader, PanelBody, PanelRow, Table, Radio, PanelFooter, Button } from '@teamsnap/teamsnap-ui';

import '../common/modal-full.scss';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';

export interface Props {
  isOpen: boolean;
  footer?: JSX.Element;
  onMergeSuccess: () => void;
  onClose: () => void;
  orgId: number;
  members: any[];
}

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const MergeMembersModal: React.FunctionComponent<Props> = ({ onClose, onMergeSuccess, isOpen, members, orgId }) => {
  const [keepMember, setKeepMember] = React.useState<any | null>(null);

  const [mergeMembers, mergeMembersResponse] = useMutation(MergeMembers.MUTATION);

  const isLoading = mergeMembersResponse.loading;

  const clearAndClose = () => {
    setKeepMember(null);
    onMergeSuccess();
  };

  const handleClick = async (_e) => {
    const duplicateMemberIds = members.filter((m) => m != keepMember).map((m) => parseInt(m.id));

    try {
      const mergeMembersResponse = await mergeMembers({
        variables: {
          organizationId: parseInt(orgId + ''),
          primaryMemberId: parseInt(keepMember.id),
          duplicateMemberIds: duplicateMemberIds,
        },
      });

      if (mergeMembersResponse?.data?.mergeMembers?.id) {
        reactiveFeedbackMessage({
          message: `${keepMember.firstName} ${keepMember.lastName} has been made the primary member.`,
          autoclose: true,
          isError: false,
        });

        clearAndClose();
      } else {
        reactiveFeedbackMessage({
          message: `Unable to merge members into ${keepMember.firstName} ${keepMember.lastName} `,
          autoclose: false,
          isError: true,
        });
      }
    } catch (e) {
      reactiveFeedbackMessage({
        message: `Unable to merge members into ${keepMember.firstName} ${keepMember.lastName} `,
        autoclose: false,
        isError: true,
      });
    }
  };

  const rows = members.map((member, index) => ({
    name: (
      <>
        <div>{member.name}</div>
        <div className="u-fontSizeSm">Created {dateTimeFormatter.format(Date.parse(member.insertedAt))}</div>
      </>
    ),
    gender: member.gender,
    birthdate: member.birthdate,
    id: member.id,
    keep: (
      <Radio
        id={`keep-member-${member.id}`}
        name={member.id}
        label="Keep"
        group="keep"
        isInline
        className="Checkbox Checkbox--radio Checkbox--pill"
        inputProps={{
          checked: member.id == keepMember?.id,
          onChange: () => {
            setKeepMember(member);
          },
        }}
      />
    ),
  }));

  return (
    <Modal isOpen={isOpen} onClose={clearAndClose}>
      <Panel>
        <PanelHeader className="Panel-header" title="Merge Members" />
        <PanelBody className="Panel-body">
          <PanelRow>
            <h4 className="u-spaceBottomSm">Which member profile would you like to keep?</h4>
            <p>
              Merging member profiles will use the data from the non-primary member profiles to fill in gaps with the
              primary profile. The non-primary profiles will then be removed from the list.
            </p>
            <Table
              mods="u-spaceTopMd"
              style={{ maxHeight: '300px', overflow: 'scroll' }}
              className="Panel"
              isLoading={isLoading}
              columns={[
                {
                  label: 'Member Name',
                  name: 'name',
                  mods: 'u-size2of8',
                },
                {
                  label: 'Gender',
                  name: 'gender',
                  mods: 'u-size1of8 u-flex u-flexCol u-flexJustifyCenter',
                },
                {
                  label: 'Birthdate',
                  name: 'birthdate',
                  mods: 'u-size1of8 u-flex u-flexCol u-flexJustifyCenter',
                },
                {
                  label: null,
                  name: 'keep',
                  align: 'right',
                  mods: 'u-size1of8',
                },
              ]}
              placeHolder="Nothing to see here"
              rows={rows}
            />
          </PanelRow>
        </PanelBody>
        <PanelFooter className="Panel-footer">
          <Button color="negative" icon="dismiss" onClick={onClose}>
            Cancel
          </Button>
          <Button mods="u-spaceLeftSm" color="primary" isDisabled={!keepMember} onClick={handleClick}>
            Merge
          </Button>
        </PanelFooter>
      </Panel>
    </Modal>
  );
};

export default MergeMembersModal;
